import { useState, useEffect } from "react";
import { Grid, CardContent, Paper, Alert, AlertTitle } from "@mui/material";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { AircraftInfo } from "./AircraftAccordion/AircraftInfo";
import { AircraftSearch } from "./AircraftSearch";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { AircraftForm } from "./Forms/Aircraft";
import { AircraftConfigurationForm } from "./Forms/Configuration";
import { AircraftConfigurationRevisionForm } from "./Forms/Revision";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import {
  IAircraft,
  IAircraftType,
  IFullAircraftConfiguration,
  IAircraftConfigurationRevision,
} from "@vmosolutions/oc-interfaces";

interface IAircraftTabProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal) => void;
  height: number;
}

export function ManageAircraft(props: IAircraftTabProps): JSX.Element {
  const { id, setActiveModal, height } = props;
  const [aircraft, setAircraft] = useState<IAircraft[]>([]);
  const [configs, setConfigs] = useState<IFullAircraftConfiguration[] | undefined>(undefined);
  const [activeAircraft, setActiveAircraft] = useState<IAircraft | null>(null);
  const [currentConfig, setCurrentConfig] = useState<IFullAircraftConfiguration | undefined>(undefined);
  const [currentRevision, setCurrentRevision] = useState<IAircraftConfigurationRevision | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [form, setForm] = useState<string>("");
  const { cancellablePromise } = useCancellablePromise();
  const [aircraftTypeOptions, setAircraftTypeOptions] = useState<IAircraftType[]>([]);

  const [stagedAircraft, setStagedAircraft] = useState<IAircraft>();
  const [stagedConfig, setStagedConfig] = useState<IFullAircraftConfiguration[]>([]);

  useEffect(() => {
    const getAircraftAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IAircraft[]>("/aircraft", undefined);
      if (Array.isArray(response)) {
        setAircraft(response);
        setIsLoading(false);
      }
    };
    getAircraftAction();
  }, []);

  useEffect(() => {
    const getTypes = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IAircraftType[]>("/aircraftTypes", undefined, cancellablePromise);
      if (Array.isArray(response)) setAircraftTypeOptions(response);
    };
    getTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAircraftList = (ac: IAircraft): void => {
    const newAircraft = [...aircraft];
    const index = aircraft.findIndex((aircraft: IAircraft) => aircraft._id === ac._id);
    if (ac._id) {
      if (index >= 0) {
        newAircraft[index] = ac;
      } else {
        newAircraft.push(ac);
      }
      setAircraft(newAircraft);
      setActiveAircraft(ac);
    }
  };

  const updateAircraftConfigurationList = (
    acConfig: IFullAircraftConfiguration,
    mode: "create" | "update" | "delete"
  ): void => {
    if (stagedAircraft) {
      setStagedConfig([acConfig]);
      setConfigs([acConfig]);
    } else {
      const newAircraftConfigOptions = configs;
      const index = newAircraftConfigOptions.findIndex(
        (aircraftConfig: IFullAircraftConfiguration) => aircraftConfig?._id === acConfig._id
      );
      switch (true) {
        case acConfig._id && mode === "update" && index > -1: // AircraftConfig Updated
          newAircraftConfigOptions[index] = acConfig;
          break;
        case acConfig._id && mode === "delete" && index > -1: // AircraftConfig Deleted
          newAircraftConfigOptions.splice(index, 1);
          break;
        default:
      }
      setConfigs(newAircraftConfigOptions);
    }
  };
  return (
    <CardContent sx={{ minHeight: `${height}px`, overflowY: "auto" }}>
      {isLoading ? (
        <Grid container>
          <Grid item sx={{ justifyContent: "center", display: "flex", margin: "auto", height: 400, padding: 40 }}>
            <LoadingWrapper message="Loading Aircraft List" />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={4}>
          <Grid container item xs={3}>
            <Grid container direction="column">
              <Paper elevation={4} sx={{ pb: 1 }}>
                <Grid item sx={{ py: 2 }}>
                  <AircraftSearch
                    isLoading={isLoading}
                    aircraft={aircraft}
                    activeAircraft={activeAircraft}
                    changeAircraft={setActiveAircraft}
                    setForm={setForm}
                    setStagedAircraft={setStagedAircraft}
                    setStagedConfig={setStagedConfig}
                    setConfigs={setConfigs}
                  />
                </Grid>
                <Grid item>
                  <AircraftInfo
                    activeAircraft={stagedAircraft ? stagedAircraft : activeAircraft}
                    configs={stagedConfig.length > 0 ? stagedConfig : configs}
                    setConfigs={setConfigs}
                    setCurrentRevision={setCurrentRevision}
                    setCurrentConfig={setCurrentConfig}
                    setActiveAircraft={setActiveAircraft}
                    setForm={setForm}
                    currentConfig={currentConfig}
                    stagedAircraft={stagedAircraft}
                    stagedConfig={stagedConfig}
                  />
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          <Grid item xs={9}>
            <Grid container alignItems="top" spacing={4}>
              <Grid item xs={12}>
                {form === "aircraft" && (
                  <AircraftForm
                    selectedAircraft={activeAircraft}
                    aircraftTypeOptions={aircraftTypeOptions}
                    updateAircraftList={updateAircraftList}
                    setForm={setForm}
                    setActiveAircraft={setActiveAircraft}
                    aircraftConfigOptions={configs}
                    stagedAircraft={stagedAircraft}
                    setStagedAircraft={setStagedAircraft}
                  />
                )}
                {form === "config" && (
                  <AircraftConfigurationForm
                    configs={configs}
                    selectedAircraft={activeAircraft}
                    selectedConfiguration={currentConfig}
                    updateAircraftConfigurationList={updateAircraftConfigurationList}
                    setForm={setForm}
                    setCurrentConfig={setCurrentConfig}
                    stagedAircraft={stagedAircraft}
                    setStagedAircraft={setStagedAircraft}
                    setStagedConfig={setStagedConfig}
                    stagedConfig={stagedConfig}
                  />
                )}
                {form === "revision" && (
                  <AircraftConfigurationRevisionForm
                    configs={configs}
                    selectedAircraft={activeAircraft}
                    selectedConfiguration={currentConfig}
                    selectedRevision={currentRevision}
                    updateAircraftConfigurationList={updateAircraftConfigurationList}
                    stagedAircraft={stagedAircraft}
                    stagedConfig={stagedConfig}
                    setStagedAircraft={setStagedAircraft}
                    updateAircraftList={updateAircraftList}
                    setStagedConfig={setStagedConfig}
                    setActiveAircraft={setActiveAircraft}
                    setConfigs={setConfigs}
                    setCurrentConfig={setCurrentConfig}
                    setCurrentRevision={setCurrentRevision}
                    setForm={setForm}
                  />
                )}
                {(!form || form === "") && (
                  <Alert severity="info" variant="filled">
                    <AlertTitle color="common.white">Getting Started</AlertTitle>
                    Add a new aircraft, or select one to get started. You can view the full list in a popup or search
                    using the dropdown menu to the left.
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </CardContent>
  );
}
